import * as React from 'react';
import { AnyAction, Dispatch } from 'redux';
import { takeEvery } from 'redux-saga/effects';
import { UploadedVideoViewModel } from 'stageclip-common';

import { AddEventVideoRequest } from '../api/definitions/addEventVideoRequest';
import { GetAllEventVideosRequest } from '../api/definitions/getAllEventVideosRequest';
import { GetAllUploadedVideosRequest } from '../api/definitions/getAllUploadedVideosRequest';
import * as EntityNormalizers from '../api/normalizers/entityNormalizers';
import * as EventVideoNormalizers from '../api/normalizers/eventVideoNormalizers';
import { DataRequestActionTypes, InternalActionTypes } from '../constants/actionTypes';

import { RequestAction } from './allActions';
import { createAllEventActionsRequestedAction } from './eventActionsActions';
import { FormSubmitAction } from './formActions';
import { runApiRequest, throttleRequests } from './saga/sagaActions';

export interface EventVideosLoadedAction extends AnyAction {
  type: InternalActionTypes.EventVideosLoaded;
  result: EntityNormalizers.NormalizedEntityArray<EventVideoNormalizers.NormalizedEventVideos>;
  eventId: string;
}

export interface EventArchivedVideosLoadedAction extends AnyAction {
  type: InternalActionTypes.EventArchivedVideosLoaded;
  result: EntityNormalizers.NormalizedEntityArray<EventVideoNormalizers.NormalizedEventVideos>;
  eventId: string;
}

export interface EventVideoLoadedAction extends AnyAction {
  type: InternalActionTypes.EventVideoLoaded;
  result: EntityNormalizers.NormalizedSingleEntity<EventVideoNormalizers.NormalizedEventVideos>;
  eventId: string;
}

export interface UploadedVideosLoadedAction extends AnyAction {
  type: InternalActionTypes.UploadedVideosLoaded;
  result: EntityNormalizers.NormalizedEntityArray<UploadedVideoViewModel[]>;
  eventId: string;
}

export interface RequestAllEventVideosAction extends RequestAction {
  type: DataRequestActionTypes.AllEventVideosRequested;
  eventId: string;
}

export interface RequestEventArchivedVideosAction extends RequestAction {
  type: DataRequestActionTypes.EventArchivedVideosRequested;
  eventId: string;
}

export interface RequestAllUploadedVideosAction extends RequestAction {
  type: DataRequestActionTypes.AllUploadedVideosRequested;
  eventId: string;
}

export interface AddEventVideoRequestedAction extends FormSubmitAction<UploadedVideoViewModel> {
  type: DataRequestActionTypes.AddEventVideoRequested;
  eventId: string;
}

export interface SendVideoReminderEmailAction extends AnyAction {
  type: InternalActionTypes.VideoReminderEmailSent;
}

function createEventVideosLoadedAction(
  eventId: string,
  result: EntityNormalizers.NormalizedEntityArray<EventVideoNormalizers.NormalizedEventVideos>,
): EventVideosLoadedAction {
  return {
    eventId,
    result,
    type: InternalActionTypes.EventVideosLoaded,
  };
}

function createEventArchivedVideosLoadedAction(
  eventId: string,
  result: EntityNormalizers.NormalizedEntityArray<EventVideoNormalizers.NormalizedEventVideos>,
): EventArchivedVideosLoadedAction {
  return {
    eventId,
    result,
    type: InternalActionTypes.EventArchivedVideosLoaded,
  };
}

function createEventVideoLoadedAction(
  eventId: string,
  result: EntityNormalizers.NormalizedSingleEntity<EventVideoNormalizers.NormalizedEventVideos>,
): EventVideoLoadedAction {
  return {
    eventId,
    result,
    type: InternalActionTypes.EventVideoLoaded,
  };
}

function createUploadedVideosLoadedAction(
  eventId: string,
  result: EntityNormalizers.NormalizedEntityArray<UploadedVideoViewModel[]>,
): UploadedVideosLoadedAction {
  return {
    eventId,
    result,
    type: InternalActionTypes.UploadedVideosLoaded,
  };
}

export function createAllEventVideosRequestedAction(
  eventId: string,
  component: React.Component,
  dispatch: Dispatch,
): RequestAllEventVideosAction {
  return {
    dispatch,
    eventId,
    requestingComponent: component,
    type: DataRequestActionTypes.AllEventVideosRequested,
  };
}

export function createEventArchivedVideosRequestedAction(
  eventId: string,
  component: React.Component,
  dispatch: Dispatch,
): RequestEventArchivedVideosAction {
  return {
    dispatch,
    eventId,
    requestingComponent: component,
    type: DataRequestActionTypes.EventArchivedVideosRequested,
  };
}

export function createAllUploadedVideosRequestedAction(
  eventId: string,
  component: React.Component,
  dispatch: Dispatch,
): RequestAllUploadedVideosAction {
  return {
    dispatch,
    eventId,
    requestingComponent: component,
    type: DataRequestActionTypes.AllUploadedVideosRequested,
  };
}

function* loadEventVideos(action: RequestAllEventVideosAction) {
  const request = new GetAllEventVideosRequest(action.eventId, action.requestingComponent, action.dispatch);
  yield runApiRequest(
    request,
    // @ts-ignore
    (result: EntityNormalizers.NormalizedEntityArray<EventVideoNormalizers.NormalizedEventVideos>) =>
      createEventVideosLoadedAction(action.eventId, result),
  );
}

function* loadEventArchivedVideos(action: RequestEventArchivedVideosAction) {
  const request = new GetAllEventVideosRequest(action.eventId, action.requestingComponent, action.dispatch);
  yield runApiRequest(
    request,
    // @ts-ignore
    (result: EntityNormalizers.NormalizedEntityArray<EventVideoNormalizers.NormalizedEventVideos>) =>
      createEventArchivedVideosLoadedAction(action.eventId, result),
  );
}

function* loadUploadedVideos(action: RequestAllUploadedVideosAction) {
  const request = new GetAllUploadedVideosRequest(action.eventId, action.requestingComponent, action.dispatch);
  // @ts-ignore
  yield runApiRequest(request, (result: EntityNormalizers.NormalizedEntityArray<UploadedVideoViewModel[]>) => {
    action.requestingComponent.setState({ uploadedVideos: result.entities });

    return createUploadedVideosLoadedAction(action.eventId, result);
  });
}

function* addEventVideo(action: AddEventVideoRequestedAction) {
  const request = new AddEventVideoRequest(
    action.eventId,
    action.formData,
    action.requestingComponent,
    action.dispatch,
  );
  yield runApiRequest(
    request,
    // @ts-ignore
    (result: EntityNormalizers.NormalizedSingleEntity<EventVideoNormalizers.NormalizedEventVideos>) => {
      action.dispatch(
        createAllEventActionsRequestedAction(action.eventId, action.requestingComponent, action.dispatch),
      );

      return createEventVideoLoadedAction(action.eventId, result);
    },
  );
}

export function* loadEventVideosSaga() {
  yield throttleRequests(
    DataRequestActionTypes.AllEventVideosRequested,
    loadEventVideos,
    (a: RequestAllEventVideosAction) => a.eventId,
  );
}

export function* loadEventArchivedVideosSaga() {
  yield throttleRequests(
    DataRequestActionTypes.EventArchivedVideosRequested,
    loadEventArchivedVideos,
    (a: RequestEventArchivedVideosAction) => a.eventId,
  );
}

export function* loadUploadedVideosSaga() {
  yield throttleRequests(
    DataRequestActionTypes.AllUploadedVideosRequested,
    loadUploadedVideos,
    (a: RequestAllUploadedVideosAction) => a.eventId,
  );
}

export function* addEventVideoSaga() {
  yield takeEvery(DataRequestActionTypes.AddEventVideoRequested, addEventVideo);
}
